import type { BodyRig } from './HumanoidGeometry';
import type { GhostPoseAnchors } from './AttachmentGeometry';
import type { GearFitMode } from './GearGeometry';
import { normalizeGearVariant } from './GearGeometry';

export type TorsoFamily = 'plate' | 'mail' | 'robe' | 'tunic' | 'vest' | 'shirt';
export type LegFamily = 'plate' | 'mail' | 'leggings' | 'pants' | 'skirt';
export type HeadFamily = 'hood' | 'helm' | 'hat' | 'crown' | 'headband' | 'mask' | 'antlers';
export type NeckFamily = 'pendant' | 'amulet' | 'necklace' | 'ring' | 'trinket';
export type GearCoverageRegion = 'head' | 'torso' | 'legs';
export type GearCoverageMode = 'frame' | 'wrap';

export interface GearCoverageGeometry {
  path: string;
  region: GearCoverageRegion;
  mode: GearCoverageMode;
  left: number;
  right: number;
  topY: number;
  bottomY: number;
}

export interface TorsoRecipeGeometry {
  coverage: GearCoverageGeometry;
  shell: string;
  neckline: string;
  hem: string;
  inset: string;
  frontStrap: string;
  backStrap: string;
  centerX: number;
  topY: number;
  hemY: number;
  left: number;
  right: number;
  fitMode: GearFitMode;
  variantTier: number;
}

export interface LegRecipeGeometry {
  coverage: GearCoverageGeometry;
  back: string;
  front: string;
  skirt: string;
  seam: string;
  centerX: number;
  topY: number;
  bottomY: number;
  fitMode: GearFitMode;
  variantTier: number;
}

export interface HeadRecipeGeometry {
  coverage?: GearCoverageGeometry;
  shell: string;
  opening: string;
  trim: string;
  accent: string;
  variantAccent: string;
  centerX: number;
  fitMode: GearFitMode;
  variantTier: number;
}

export interface CloakRecipeGeometry {
  shell: string;
  hem: string;
  centerX: number;
  fitMode: GearFitMode;
  variantTier: number;
}

export interface NeckRecipeGeometry {
  chain: string;
  centerX: number;
  ornamentY: number;
  fitMode: GearFitMode;
  variantTier: number;
}

export interface ShoulderRecipeGeometry {
  back: string;
  front: string;
}

export interface SleeveRecipeGeometry {
  back: string;
  front: string;
  backCuff: { x: number; y: number };
  frontCuff: { x: number; y: number };
  width: number;
  length: number;
  variantTier: number;
}

function n(value: number): string {
  return String(Number(value.toFixed(2)));
}

function wrapBounds(
  rig: BodyRig,
  shell: boolean,
): { left: number; right: number } {
  return {
    left: Math.max(3, rig.silhouette.left - (shell ? 4 : 3)),
    // Keep replacement silhouettes inside the airborne rotation envelope.
    // The visible front construction may still grow inward by family/tier.
    right: Math.min(134, rig.silhouette.right + (shell ? 5 : 4)),
  };
}

function wrapCoverage(
  rig: BodyRig,
  region: 'torso' | 'legs',
  topY: number,
  bottomY: number,
  shell: boolean,
): GearCoverageGeometry {
  const { left, right } = wrapBounds(rig, shell);
  const midY = (topY + bottomY) / 2;
  // Coverage cannot taper inside the declared body at the hem: that reveals
  // a one-pixel strip of the base ghost on round/wide silhouettes.
  const bottomInset = 0;
  const path = [
    `M${n(left)},${n(topY)}`,
    `Q${n(left - 1)},${n(midY)} ${n(left + bottomInset)},${n(bottomY)}`,
    `Q${n(rig.centerX)},${n(bottomY + (shell ? 6 : 4))} ${n(right - bottomInset)},${n(bottomY)}`,
    `Q${n(right + 1)},${n(midY)} ${n(right)},${n(topY)}`,
    `Q${n(rig.centerX)},${n(topY + 7)} ${n(left)},${n(topY)} Z`,
  ].join(' ');

  return {
    path,
    region,
    mode: 'wrap',
    left,
    right,
    topY,
    bottomY,
  };
}

function torsoFitMode(family: TorsoFamily): GearFitMode {
  return family === 'plate' || family === 'mail' ? 'shell' : 'surface';
}

export function torsoRecipe(
  rig: BodyRig,
  family: TorsoFamily,
  variant: number,
): TorsoRecipeGeometry {
  const tier = normalizeGearVariant(variant);
  const { shoulders, neck, waist, hem } = rig.torso;
  const fitMode = torsoFitMode(family);

  const profile = {
    plate: { shoulderInset: -1.5, waistInset: -1, hemInset: -1, hemDelta: 1, coverageDelta: 8, neckDepthDelta: -3, hemCurve: 3 },
    mail: { shoulderInset: 0, waistInset: 0, hemInset: 0, hemDelta: 0, coverageDelta: 8, neckDepthDelta: -1, hemCurve: 4 },
    robe: { shoulderInset: 0, waistInset: -1, hemInset: -5, hemDelta: 14, coverageDelta: 1, neckDepthDelta: 1, hemCurve: 5 },
    tunic: { shoulderInset: 2, waistInset: 1, hemInset: 1, hemDelta: 0, coverageDelta: 8, neckDepthDelta: 0, hemCurve: 4 },
    vest: { shoulderInset: 5, waistInset: 4, hemInset: 5, hemDelta: -6, coverageDelta: 5, neckDepthDelta: 2, hemCurve: 3 },
    shirt: { shoulderInset: 3, waistInset: 2, hemInset: 3, hemDelta: -2, coverageDelta: 7, neckDepthDelta: 1, hemCurve: 4 },
  }[family];

  const shellGrowth = fitMode === 'shell' ? tier * 0.35 : tier * 0.12;
  const shoulderLeft = shoulders.left + profile.shoulderInset - shellGrowth;
  const shoulderRight = shoulders.right - profile.shoulderInset + shellGrowth;
  const waistLeft = waist.left + profile.waistInset - shellGrowth * 0.5;
  const waistRight = waist.right - profile.waistInset + shellGrowth * 0.5;
  const hemLeft = hem.left + profile.hemInset - shellGrowth;
  const hemRight = hem.right - profile.hemInset + shellGrowth;
  const hemY = Math.min(109, hem.y + profile.hemDelta + tier * 0.15);
  const topY = shoulders.y;
  const coverage = wrapCoverage(
    rig,
    'torso',
    topY - 3,
    Math.min(112, hemY + profile.coverageDelta),
    fitMode === 'shell',
  );
  const neckDepthY = neck.depthY + profile.neckDepthDelta;
  const middleY = (topY + waist.y) / 2;

  const shell = [
    `M${n(shoulderLeft)},${n(topY + 1)}`,
    `Q${n(shoulderLeft + 4)},${n(topY - 6)} ${n(neck.left)},${n(neck.y)}`,
    `Q${n(rig.centerX)},${n(neckDepthY)} ${n(neck.right)},${n(neck.y)}`,
    `Q${n(shoulderRight - 4)},${n(topY - 6)} ${n(shoulderRight)},${n(topY + 1)}`,
    `Q${n(shoulderRight + 1)},${n(middleY)} ${n(waistRight)},${n(waist.y)}`,
    `L${n(hemRight)},${n(hemY)}`,
    `Q${n(rig.centerX)},${n(hemY + profile.hemCurve)} ${n(hemLeft)},${n(hemY)}`,
    `L${n(waistLeft)},${n(waist.y)}`,
    `Q${n(shoulderLeft - 1)},${n(middleY)} ${n(shoulderLeft)},${n(topY + 1)} Z`,
  ].join(' ');

  const neckline = `M${n(neck.left)},${n(neck.y)} Q${n(rig.centerX)},${n(neckDepthY)} ${n(neck.right)},${n(neck.y)}`;
  const hemPath = `M${n(hemLeft + 2)},${n(hemY - 3)} Q${n(rig.centerX)},${n(hemY + 1)} ${n(hemRight - 2)},${n(hemY - 3)}`;
  const insetTop = neckDepthY + 1;
  const insetBottom = Math.min(hemY - 7, waist.y + 5);
  const insetWidth = Math.max(10, (waistRight - waistLeft) * 0.28);
  const inset = [
    `M${n(rig.centerX - insetWidth)},${n(insetTop)}`,
    `L${n(rig.centerX + insetWidth)},${n(insetTop - 1)}`,
    `L${n(rig.centerX + insetWidth * 0.82)},${n(insetBottom)}`,
    `Q${n(rig.centerX)},${n(insetBottom + 4)} ${n(rig.centerX - insetWidth * 0.82)},${n(insetBottom)} Z`,
  ].join(' ');
  const frontStrap = `M${n(shoulderLeft + 4)},${n(topY + 1)} L${n(hemRight - 3)},${n(hemY - 2)}`;
  const backStrap = `M${n(shoulderRight - 3)},${n(topY)} L${n(hemLeft + 4)},${n(hemY - 2)}`;

  return {
    coverage,
    shell,
    neckline,
    hem: hemPath,
    inset,
    frontStrap,
    backStrap,
    centerX: rig.centerX,
    topY,
    hemY,
    left: shoulderLeft,
    right: shoulderRight,
    fitMode,
    variantTier: tier,
  };
}

const SLEEVE_PROFILES: Record<TorsoFamily, { length: number; width: number }> = {
  plate: { length: 0.84, width: 12.5 },
  mail: { length: 0.94, width: 11.5 },
  robe: { length: 0.98, width: 12 },
  tunic: { length: 0.96, width: 11.5 },
  vest: { length: 0.38, width: 11 },
  shirt: { length: 0.9, width: 11 },
};

function sleeveSegment(
  shoulder: { x: number; y: number },
  hand: { x: number; y: number },
  length: number,
): { path: string; cuff: { x: number; y: number } } {
  const control = {
    x: (shoulder.x + hand.x) / 2,
    y: (shoulder.y + hand.y) / 2 + 3,
  };
  // De Casteljau subdivision keeps the sleeve exactly on the same quadratic
  // curve as the underlying animated arm for any pose and body rig.
  const sleeveControl = {
    x: shoulder.x + (control.x - shoulder.x) * length,
    y: shoulder.y + (control.y - shoulder.y) * length,
  };
  const oneMinus = 1 - length;
  const cuff = {
    x: oneMinus * oneMinus * shoulder.x
      + 2 * oneMinus * length * control.x
      + length * length * hand.x,
    y: oneMinus * oneMinus * shoulder.y
      + 2 * oneMinus * length * control.y
      + length * length * hand.y,
  };
  return {
    path: `M${n(shoulder.x)},${n(shoulder.y)} Q${n(sleeveControl.x)},${n(sleeveControl.y)} ${n(cuff.x)},${n(cuff.y)}`,
    cuff,
  };
}

/**
 * Moving garment coverage for the two explicit arm paths.
 *
 * This is recipe geometry, not another skeleton: it subdivides the exact
 * shoulder-to-hand Bézier already used by GhostRenderer.
 */
export function sleeveRecipe(
  rig: BodyRig,
  family: TorsoFamily,
  variant: number,
  anchors: GhostPoseAnchors,
): SleeveRecipeGeometry {
  const tier = normalizeGearVariant(variant);
  const profile = SLEEVE_PROFILES[family];
  const length = Math.min(0.99, profile.length + tier * 0.006);
  const width = profile.width + tier * 0.22;
  const back = sleeveSegment(
    rig.attachments.shoulders.back,
    anchors.backHand,
    length,
  );
  const front = sleeveSegment(
    rig.attachments.shoulders.front,
    anchors.frontHand,
    length,
  );

  return {
    back: back.path,
    front: front.path,
    backCuff: back.cuff,
    frontCuff: front.cuff,
    width,
    length,
    variantTier: tier,
  };
}

function legPanelPath(
  centerX: number,
  width: number,
  topY: number,
  bottomY: number,
  slant: number,
): string {
  const half = width / 2;
  return [
    `M${n(centerX - half)},${n(topY)}`,
    `Q${n(centerX)},${n(topY + 2)} ${n(centerX + half)},${n(topY)}`,
    `L${n(centerX + half * 0.86 + slant)},${n(bottomY - 1)}`,
    `Q${n(centerX)},${n(bottomY + 1)} ${n(centerX - half * 0.86 + slant)},${n(bottomY - 1)} Z`,
  ].join(' ');
}

export function legRecipe(
  rig: BodyRig,
  family: LegFamily,
  variant: number,
): LegRecipeGeometry {
  const tier = normalizeGearVariant(variant);
  const fitMode: GearFitMode = family === 'plate' || family === 'mail' ? 'shell' : 'surface';
  const widthFactor = {
    plate: 1.12,
    mail: 1.02,
    leggings: 0.92,
    pants: 1,
    skirt: 1,
  }[family];
  const width = rig.legs.width * widthFactor + tier * 0.35;
  const topY = rig.legs.topY;
  const bottomY = Math.min(111, rig.legs.bottomY + (family === 'plate' ? 1 : 0));
  const coverage = wrapCoverage(
    rig,
    'legs',
    topY - 3,
    Math.min(113, bottomY + 3),
    fitMode === 'shell',
  );
  const back = legPanelPath(rig.legs.backCenterX, width, topY, bottomY, -0.6);
  const front = legPanelPath(rig.legs.frontCenterX, width, topY, bottomY, 0.6);
  const skirtLeft = rig.legs.backCenterX - width * 0.72;
  const skirtRight = rig.legs.frontCenterX + width * 0.72;
  const skirt = [
    `M${n(skirtLeft + 2)},${n(topY)}`,
    `Q${n(rig.centerX)},${n(topY + 4)} ${n(skirtRight - 2)},${n(topY)}`,
    `L${n(skirtRight)},${n(bottomY)}`,
    `Q${n(rig.centerX)},${n(bottomY + 2)} ${n(skirtLeft)},${n(bottomY)} Z`,
  ].join(' ');
  const seam = `M${n(rig.centerX)},${n(topY + 3)} L${n(rig.centerX)},${n(bottomY - 1)}`;

  return {
    coverage,
    back,
    front,
    skirt,
    seam,
    centerX: rig.centerX,
    topY,
    bottomY,
    fitMode,
    variantTier: tier,
  };
}

function ringShell(
  outer: string,
  opening: string,
): string {
  return `${outer} ${opening}`;
}

function headVariantAccent(
  centerX: number,
  topY: number,
  openingTopY: number,
  tier: number,
): string {
  const crownY = Math.max(topY + 6, openingTopY - 10);
  const lowerY = openingTopY - 2;
  if (tier === 1) {
    return `M${n(centerX)},${n(crownY)} L${n(centerX)},${n(lowerY)}`;
  }
  if (tier === 2) {
    return `M${n(centerX - 10)},${n(lowerY - 3)} L${n(centerX)},${n(lowerY + 2)} L${n(centerX + 10)},${n(lowerY - 3)}`;
  }
  if (tier === 3) {
    return `M${n(centerX)},${n(crownY)} L${n(centerX + 5)},${n(crownY + 5)} L${n(centerX)},${n(crownY + 10)} L${n(centerX - 5)},${n(crownY + 5)} Z`;
  }
  if (tier === 4) {
    return [
      `M${n(centerX - 11)},${n(crownY + 2)} L${n(centerX)},${n(crownY + 8)} L${n(centerX + 11)},${n(crownY + 2)}`,
      `M${n(centerX - 9)},${n(crownY + 9)} L${n(centerX)},${n(crownY + 14)} L${n(centerX + 9)},${n(crownY + 9)}`,
    ].join(' ');
  }
  if (tier === 5) {
    return [
      `M${n(centerX)},${n(crownY)} L${n(centerX + 5)},${n(crownY + 5)} L${n(centerX)},${n(crownY + 10)} L${n(centerX - 5)},${n(crownY + 5)} Z`,
      `M${n(centerX - 13)},${n(lowerY - 2)} Q${n(centerX)},${n(lowerY + 5)} ${n(centerX + 13)},${n(lowerY - 2)}`,
    ].join(' ');
  }
  return '';
}

export function headRecipe(
  rig: BodyRig,
  family: HeadFamily,
  variant: number,
): HeadRecipeGeometry {
  const tier = normalizeGearVariant(variant);
  const h = rig.head;
  const centerX = (h.openingLeft + h.openingRight) / 2;
  const topY = h.topY - tier * 0.45;
  let shell = '';
  let opening = '';
  let trim = '';
  let accent = '';
  let variantAccent = '';
  let fitMode: GearFitMode = 'shell';
  let coverage: GearCoverageGeometry | undefined;

  if (family === 'hood' || family === 'helm') {
    const openingInset = family === 'helm' ? 2 : 0;
    const outerDrop = family === 'hood' ? 9 : 4;
    const wrap = wrapBounds(rig, true);
    // Keep the visible front shell tied to the authored head landmarks. The
    // coverage layer below it owns the exact body-edge replacement, so the
    // shell does not need to inflate to the full torso silhouette (which
    // would crop in rotated airborne poses on wide bodies).
    const outerFlare = family === 'hood' ? 4 : 2;
    const outerBaseLeft = Math.max(wrap.left, h.outerLeft - outerFlare);
    const outerBaseRight = Math.min(wrap.right, h.outerRight + outerFlare);
    const outerControlLeft = Math.max(
      outerBaseLeft + 2,
      h.outerLeft - 10,
    );
    const outerControlRight = Math.min(
      outerBaseRight - 2,
      h.outerRight + 10,
    );
    const openingBottomY = family === 'hood'
      ? h.openingBottomY
      : h.openingBottomY - 5;
    const openingDrop = family === 'hood' ? 5 : 1;
    const outer = [
      `M${n(outerBaseLeft)},${n(h.baseY + 3)}`,
      `Q${n(outerControlLeft)},${n(topY + 12)} ${n(centerX)},${n(topY)}`,
      `Q${n(outerControlRight)},${n(topY + 11)} ${n(outerBaseRight)},${n(h.baseY + 3)}`,
      `Q${n(centerX)},${n(h.baseY + outerDrop + 3)} ${n(outerBaseLeft)},${n(h.baseY + 3)} Z`,
    ].join(' ');
    opening = [
      `M${n(h.openingLeft + openingInset)},${n(openingBottomY)}`,
      `Q${n(h.openingLeft + openingInset)},${n(h.openingTopY + 5)} ${n(centerX)},${n(h.openingTopY)}`,
      `Q${n(h.openingRight - openingInset)},${n(h.openingTopY + 4)} ${n(h.openingRight - openingInset)},${n(openingBottomY)}`,
      `Q${n(centerX)},${n(openingBottomY + openingDrop)} ${n(h.openingLeft + openingInset)},${n(openingBottomY)} Z`,
    ].join(' ');
    shell = ringShell(outer, opening);
    coverage = {
      path: ringShell(h.coverageOuterPath, opening),
      region: 'head',
      mode: 'frame',
      left: rig.silhouette.left,
      right: rig.silhouette.right,
      topY: h.topY,
      bottomY: h.baseY + outerDrop + 3,
    };
    trim = [
      `M${n(h.openingLeft + openingInset)},${n(openingBottomY)}`,
      `Q${n(h.openingLeft + openingInset)},${n(h.openingTopY + 5)} ${n(centerX)},${n(h.openingTopY)}`,
      `Q${n(h.openingRight - openingInset)},${n(h.openingTopY + 4)} ${n(h.openingRight - openingInset)},${n(openingBottomY)}`,
    ].join(' ');
    const helmTipY = Math.max(1, topY - 8 - tier);
    accent = family === 'helm'
      ? `M${n(centerX)},${n(topY + 1)} L${n(centerX + 5)},${n(helmTipY)} L${n(centerX + 10)},${n(topY + 2)}`
      : `M${n(h.openingLeft + 4)},${n(openingBottomY + 1)} Q${n(centerX)},${n(openingBottomY + 7)} ${n(h.openingRight - 4)},${n(openingBottomY + 1)}`;
    variantAccent = headVariantAccent(centerX, topY, h.openingTopY, tier);
  } else if (family === 'hat') {
    fitMode = 'free';
    const brimY = h.openingTopY + 11;
    shell = `M${n(h.openingLeft - 8)},${n(brimY)} Q${n(centerX)},${n(topY + 6)} ${n(h.openingRight + 8)},${n(brimY)} L${n(h.openingRight + 4)},${n(brimY + 7)} Q${n(centerX)},${n(brimY + 2)} ${n(h.openingLeft - 4)},${n(brimY + 7)} Z`;
    accent = `M${n(h.openingLeft - 2)},${n(brimY - 1)} Q${n(centerX)},${n(brimY - 5)} ${n(h.openingRight + 2)},${n(brimY - 1)}`;
  } else if (family === 'crown') {
    fitMode = 'free';
    const left = h.openingLeft + 4;
    const right = h.openingRight - 4;
    const baseY = h.openingTopY + 12;
    const span = right - left;
    shell = `M${n(left)},${n(baseY)} L${n(left + span * 0.08)},${n(baseY - 13 - tier)} L${n(left + span * 0.28)},${n(baseY - 6)} L${n(centerX)},${n(baseY - 17 - tier)} L${n(left + span * 0.72)},${n(baseY - 6)} L${n(right - span * 0.08)},${n(baseY - 13 - tier)} L${n(right)},${n(baseY)} Z`;
    accent = `M${n(left + 2)},${n(baseY - 3)} L${n(right - 2)},${n(baseY - 3)}`;
  } else if (family === 'headband') {
    fitMode = 'anchored';
    const y = h.openingTopY + 8;
    shell = `M${n(h.openingLeft)},${n(y)} Q${n(centerX)},${n(y - 6)} ${n(h.openingRight)},${n(y)} L${n(h.openingRight - 1)},${n(y + 6)} Q${n(centerX)},${n(y)} ${n(h.openingLeft + 1)},${n(y + 6)} Z`;
    accent = `M${n(h.openingLeft + 2)},${n(y + 2)} Q${n(centerX)},${n(y - 2)} ${n(h.openingRight - 2)},${n(y + 2)}`;
  } else if (family === 'mask') {
    fitMode = 'anchored';
    const y = h.openingTopY + 16;
    shell = `M${n(h.openingLeft + 4)},${n(y)} Q${n(centerX)},${n(y - 5)} ${n(h.openingRight - 4)},${n(y)} L${n(h.openingRight - 6)},${n(y + 11)} Q${n(centerX)},${n(y + 16)} ${n(h.openingLeft + 6)},${n(y + 11)} Z`;
    trim = `M${n(h.openingLeft + 8)},${n(y + 4)} L${n(centerX - 4)},${n(y + 3)} M${n(centerX + 4)},${n(y + 3)} L${n(h.openingRight - 8)},${n(y + 4)}`;
  } else {
    fitMode = 'free';
    const rootY = h.openingTopY + 5;
    shell = [
      `M${n(centerX - 14)},${n(rootY)} Q${n(centerX - 25)},${n(rootY - 12)} ${n(centerX - 28)},${n(rootY - 24 - tier)}`,
      `M${n(centerX - 19)},${n(rootY - 12)} L${n(centerX - 30)},${n(rootY - 10)}`,
      `M${n(centerX + 14)},${n(rootY)} Q${n(centerX + 25)},${n(rootY - 12)} ${n(centerX + 28)},${n(rootY - 24 - tier)}`,
      `M${n(centerX + 19)},${n(rootY - 12)} L${n(centerX + 30)},${n(rootY - 10)}`,
    ].join(' ');
  }

  return {
    coverage,
    shell,
    opening,
    trim,
    accent,
    variantAccent,
    centerX,
    fitMode,
    variantTier: tier,
  };
}

export function cloakRecipe(rig: BodyRig, variant: number): CloakRecipeGeometry {
  const tier = normalizeGearVariant(variant);
  const { shoulders, hem } = rig.cloak;
  const shoulderGrowth = tier * 0.25;
  const hemY = Math.min(114, hem.y + tier * 0.45);
  const shell = [
    `M${n(shoulders.left - shoulderGrowth)},${n(shoulders.y)}`,
    `Q${n(rig.centerX)},${n(shoulders.y - 8 - tier * 0.3)} ${n(shoulders.right + shoulderGrowth)},${n(shoulders.y)}`,
    `L${n(hem.right + shoulderGrowth)},${n(hemY)}`,
    `Q${n(rig.centerX)},${n(hemY + 5)} ${n(hem.left - shoulderGrowth)},${n(hemY)} Z`,
  ].join(' ');
  const hemPath = `M${n(hem.left + 2)},${n(hemY - 3)} Q${n(rig.centerX)},${n(hemY + 2)} ${n(hem.right - 2)},${n(hemY - 3)}`;
  return {
    shell,
    hem: hemPath,
    centerX: rig.centerX,
    fitMode: 'free',
    variantTier: tier,
  };
}

export function neckRecipe(
  rig: BodyRig,
  family: NeckFamily,
  variant: number,
): NeckRecipeGeometry {
  const tier = normalizeGearVariant(variant);
  const neck = rig.torso.neck;
  const drop = {
    pendant: 10,
    amulet: 14,
    necklace: 7,
    ring: 9,
    trinket: 12,
  }[family] + tier * 0.35;
  const startY = neck.y + 2;
  const ornamentY = neck.depthY + drop;
  const chain = `M${n(neck.left + 1)},${n(startY)} Q${n(rig.centerX)},${n(ornamentY)} ${n(neck.right - 1)},${n(startY)}`;
  return {
    chain,
    centerX: rig.centerX,
    ornamentY,
    fitMode: 'anchored',
    variantTier: tier,
  };
}

export function shoulderRecipe(rig: BodyRig, heavy: boolean): ShoulderRecipeGeometry {
  const shoulders = rig.torso.shoulders;
  const backRoot = rig.attachments.shoulders.back;
  const frontRoot = rig.attachments.shoulders.front;
  const width = heavy ? 17 : 13;
  const height = heavy ? 13 : 9;
  return {
    back: `M${n(backRoot.x - width)},${n(shoulders.y)} Q${n(backRoot.x - width * 0.45)},${n(shoulders.y - height)} ${n(backRoot.x + 3)},${n(shoulders.y - 4)} L${n(backRoot.x + 5)},${n(shoulders.y + 6)} Q${n(backRoot.x - 5)},${n(shoulders.y + 2)} ${n(backRoot.x - width)},${n(shoulders.y + 8)} Z`,
    front: `M${n(frontRoot.x - 5)},${n(shoulders.y - 4)} Q${n(frontRoot.x + width * 0.45)},${n(shoulders.y - height)} ${n(frontRoot.x + width)},${n(shoulders.y)} L${n(frontRoot.x + width - 2)},${n(shoulders.y + 9)} Q${n(frontRoot.x + 6)},${n(shoulders.y + 2)} ${n(frontRoot.x - 5)},${n(shoulders.y + 6)} Z`,
  };
}
